/**
 * Aipron CoPilot™ - Chat Interface Component
 * ==========================================
 * Floating chat panel for conversational AI interactions.
 * Context-aware: receives page context from CoPilotContext.
 * Enhanced with ActionCard, NavigationLink, and QueryResult components.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Bot, User, Loader2, Sparkles, Trash2, MapPin } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useCoPilotContext } from '../../context/CoPilotContext';
import {
    sendMessage,
    clearSession,
    generateMessageId,
    generateSessionId,
    ChatMessage,
    ActionProposal,
    NavigationLink as NavigationLinkData,
} from '../../services/copilot';
import ActionCard from './ActionCard';
import NavigationLink from './NavigationLink';

// --- Texts ---
const TEXTS = {
    es: {
        title: 'Aipron CoPilot™',
        subtitle: 'Asistente IA para AirportOS',
        placeholder: 'Pregunta sobre operaciones...',
        send: 'Enviar',
        clear: 'Limpiar chat',
        sources: 'Fuentes',
        thinking: 'Analizando...',
        welcome: '¡Hola! Soy CoPilot, tu asistente de operaciones aeroportuarias. Puedo consultar vuelos, navegar a pantallas, comparar escenarios o explicar KPIs. ¿En qué te ayudo?',
        examples: [
            'Compara el proyecto Madrid con Barcelona',
            '¿Por qué el BCI es bajo?',
            'Muéstrame cuellos de botella para mañana',
        ],
    },
    en: {
        title: 'Aipron CoPilot™',
        subtitle: 'AI Assistant for AirportOS',
        placeholder: 'Ask about operations...',
        send: 'Send',
        clear: 'Clear chat',
        sources: 'Sources',
        thinking: 'Analyzing...',
        welcome: 'Hello! I\'m CoPilot, your airport operations assistant. I can query flights, compare scenarios, explain KPIs, or execute actions. How can I help?',
        examples: [
            'Compare project A vs project B',
            'Why is the AXOT so high?',
            'Show me bottlenecks for tomorrow',
        ],
    },
};

interface ChatInterfaceProps {
    projectId: string;
    isOpen: boolean;
    onClose: () => void;
    /** Docked into the shell's side column: fill the panel instead of floating. */
    docked?: boolean;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ projectId, isOpen, onClose, docked = false }) => {
    const { language } = useLanguage();
    const { pageContext, getContextSummary } = useCoPilotContext();
    const t = TEXTS[language as 'es' | 'en'] || TEXTS.en;

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string>(() => generateSessionId());

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    // Add welcome message on first open
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages([
                {
                    id: generateMessageId(),
                    role: 'assistant',
                    content: t.welcome,
                    timestamp: new Date(),
                },
            ]);
        }
    }, [isOpen, messages.length, t.welcome]);

    const handleSend = useCallback(async () => {
        if (!input.trim() || isLoading) return;

        const userMessage: ChatMessage = {
            id: generateMessageId(),
            role: 'user',
            content: input.trim(),
            timestamp: new Date(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            // Include page context in the message
            const contextSummary = getContextSummary();
            const messageWithContext = contextSummary
                ? `${contextSummary}\n\nUser question: ${userMessage.content}`
                : userMessage.content;

            const response = await sendMessage(messageWithContext, projectId, sessionId, true);

            const assistantMessage: ChatMessage = {
                id: generateMessageId(),
                role: 'assistant',
                content: response.response,
                timestamp: new Date(),
                sources: response.sources,
                actions: response.actions,
                navigation: response.navigation,
            };

            setMessages((prev) => [...prev, assistantMessage]);
            setSessionId(response.session_id);
        } catch (error) {
            const errorMessage: ChatMessage = {
                id: generateMessageId(),
                role: 'assistant',
                content: '❌ Error connecting to CoPilot. Please try again.',
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    }, [input, isLoading, projectId, sessionId, getContextSummary]);

    const handleClear = useCallback(async () => {
        await clearSession(sessionId);
        setMessages([]);
        setSessionId(generateSessionId());
    }, [sessionId]);

    const handleExampleClick = (example: string) => {
        setInput(example);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleActionComplete = (success: boolean, message: string) => {
        // Add confirmation message
        const confirmMessage: ChatMessage = {
            id: generateMessageId(),
            role: 'assistant',
            content: success ? `✅ ${message}` : `❌ ${message}`,
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, confirmMessage]);
    };

    const handleNavigate = () => {
        // Optionally close chat on navigation
        // onClose();
    };

    if (!isOpen) return null;

    // Styles (design-system tokens only — theme-aware via CSS custom properties)
    const panelBg = 'bg-[var(--surface)]';
    const headerBg = 'bg-[var(--bg)]';
    const borderColor = 'border-[var(--border)]';
    const textPrimary = 'text-[var(--text-primary)]';
    const textSecondary = 'text-[var(--text-secondary)]';
    const inputBg = 'bg-[var(--surface-raised)] border border-[var(--border)]';

    return (
        <div
            className={docked
                ? 'h-full w-full flex flex-col overflow-hidden animate-fade-in'
                : 'fixed bottom-4 right-4 w-[420px] h-[600px] z-[60] flex flex-col rounded-2xl shadow-2xl overflow-hidden border animate-fade-in'}
            style={docked ? undefined : { borderColor: 'var(--border)' }}
        >
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${headerBg} ${borderColor}`}>
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl" style={{ background: 'var(--aipron-accent)' }}>
                        <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className={`font-bold ${textPrimary}`}>{t.title}</h3>
                        <div className="flex items-center gap-2">
                            <p className={`text-xs ${textSecondary}`}>{t.subtitle}</p>
                            {pageContext && (
                                <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full"
                                      style={{ background: 'var(--accent-soft)', color: 'var(--aipron-accent)' }}>
                                    <MapPin size={10} />
                                    {pageContext.pageName}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleClear}
                        className={`p-2 rounded-lg hover:bg-[var(--surface-inset)] transition-colors ${textSecondary}`}
                        title={t.clear}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg hover:bg-[var(--surface-inset)] transition-colors ${textSecondary}`}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${panelBg}`}>
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                        {/* Avatar — user = solid accent, assistant = neutral surface */}
                        <div
                            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                            style={msg.role === 'user'
                                ? { background: 'var(--aipron-accent)' }
                                : { background: 'var(--surface-inset)' }}
                        >
                            {msg.role === 'user' ? (
                                <User className="w-4 h-4 text-white" />
                            ) : (
                                <Bot className="w-4 h-4" style={{ color: 'var(--aipron-accent)' }} />
                            )}
                        </div>

                        {/* Message bubble */}
                        <div
                            className="max-w-[80%] rounded-2xl px-4 py-3"
                            style={msg.role === 'user'
                                ? { background: 'var(--aipron-accent)', color: '#fff' }
                                : { background: 'var(--surface-inset)', color: 'var(--text-primary)' }}
                        >
                            <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>

                            {/* Navigation Links */}
                            {msg.navigation && msg.navigation.length > 0 && (
                                <div className="mt-3 space-y-2">
                                    {msg.navigation.map((link, i) => (
                                        <NavigationLink
                                            key={i}
                                            link={link}
                                            onNavigate={handleNavigate}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Action Cards */}
                            {msg.actions && msg.actions.length > 0 && (
                                <div className="mt-3 space-y-2">
                                    {msg.actions.map((action, i) => (
                                        <ActionCard
                                            key={i}
                                            action={action}
                                            onActionComplete={handleActionComplete}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Sources */}
                            {msg.sources && msg.sources.length > 0 && (
                                <div className={`mt-3 pt-2 border-t ${'border-[var(--border)]'}`}>
                                    <p className="text-[10px] uppercase tracking-wide mb-1 text-[var(--text-tertiary)]">
                                        {t.sources}
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {msg.sources.map((source, i) => (
                                            <span
                                                key={i}
                                                className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--surface-inset)] text-[var(--text-secondary)]"
                                            >
                                                {source}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {/* Typing indicator */}
                {isLoading && (
                    <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'var(--surface-inset)' }}>
                            <Bot className="w-4 h-4" style={{ color: 'var(--aipron-accent)' }} />
                        </div>
                        <div className="rounded-2xl px-4 py-3 bg-[var(--surface-inset)]">
                            <div className="flex items-center gap-2">
                                <Loader2 className={`w-4 h-4 animate-spin ${textSecondary}`} />
                                <span className={`text-sm ${textSecondary}`}>{t.thinking}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Example prompts (shown when only welcome message exists) */}
                {messages.length === 1 && (
                    <div className="p-4 rounded-xl border border-[var(--border)] bg-[var(--surface-raised)]">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-4 h-4" style={{ color: 'var(--aipron-accent)' }} />
                            <span className={`text-xs font-medium ${textSecondary}`}>Try asking:</span>
                        </div>
                        <div className="space-y-2">
                            {t.examples.map((example, i) => (
                                <button
                                    key={i}
                                    onClick={() => handleExampleClick(example)}
                                    className="w-full text-left text-sm px-3 py-2 rounded-lg transition-colors hover:bg-[var(--surface-inset)] text-[var(--text-secondary)]"
                                >
                                    "{example}"
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className={`p-4 border-t ${borderColor} ${headerBg}`}>
                <div className={`flex items-center gap-2 p-2 rounded-xl ${inputBg}`}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t.placeholder}
                        disabled={isLoading}
                        className={`flex-1 bg-transparent border-none outline-none text-sm px-2 ${textPrimary} placeholder:${textSecondary}`}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!input.trim() || isLoading}
                        className={`p-2 rounded-lg transition-all ${input.trim() && !isLoading
                            ? 'text-white hover:opacity-90'
                            : 'text-[var(--text-tertiary)] cursor-not-allowed'
                            }`}
                        style={{ background: input.trim() && !isLoading ? 'var(--aipron-accent)' : 'var(--surface-inset)' }}
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatInterface;
